'use client';

import { useState } from 'react';
import Button from '@/components/ui/Button';
import { trackEvent } from '@/lib/analytics';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const SERVICE_OPTIONS = [
  'Caretaking',
  'Winter',
  'Summer',
  'Cleaning & Rentals',
  'Permits',
  'Insurance & Admin',
  'Projects',
];

// Real, working fallback: the site's ContactForm is a stub (console.log only) and
// there is no contact API route, so we compose a mailto to the office instead.
const CONTACT_EMAIL = 'info@kettura.com';

const inputClasses =
  'w-full rounded-lg px-4 py-3 text-sm outline-none transition-colors duration-300 bg-white border border-[#5A5A6E]/20 text-[#1A1A2E] placeholder-[#5A5A6E] focus:border-[#9B7B3A]';

export default function PropertyServicesForm({
  nameLabel = 'Name',
  namePlaceholder = 'Your name',
  emailLabel = 'Email',
  emailPlaceholder = 'your@email.com',
  locationLabel = 'Property location',
  locationPlaceholder = 'e.g. Äkäslompolo, Ylläs',
  servicesLabel = "Services you're interested in",
  serviceOptions = SERVICE_OPTIONS,
  messageLabel = 'Message',
  messagePlaceholder = "Write in your own language - we'll reply in it.",
  submitLabel = 'Request a care plan',
}: {
  nameLabel?: string;
  namePlaceholder?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  locationLabel?: string;
  locationPlaceholder?: string;
  servicesLabel?: string;
  serviceOptions?: string[];
  messageLabel?: string;
  messagePlaceholder?: string;
  submitLabel?: string;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  const [services, setServices] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');

  const toggleService = (service: string) => {
    setServices((prev) => {
      const next = new Set(prev);
      if (next.has(service)) next.delete(service);
      else next.add(service);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackEvent('property_services_enquiry', {
      services: Array.from(services).join(', ') || 'none',
    });

    const selected = serviceOptions.filter((s) => services.has(s));
    const body = [
      `Name: ${name}`,
      `Email: ${email}`,
      `Property location: ${location}`,
      `Services of interest: ${selected.length ? selected.join(', ') : '-'}`,
      '',
      'Message:',
      message,
    ].join('\n');

    const href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      `Property Services enquiry - ${name || 'Kettura'}`
    )}&body=${encodeURIComponent(body)}`;

    window.location.href = href;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="ps-name" className="block text-sm font-medium mb-2 text-[#1A1A2E]">
            {nameLabel}
          </label>
          <input
            id="ps-name"
            type="text"
            placeholder={namePlaceholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClasses}
            required
          />
        </div>

        <div>
          <label htmlFor="ps-email" className="block text-sm font-medium mb-2 text-[#1A1A2E]">
            {emailLabel}
          </label>
          <input
            id="ps-email"
            type="email"
            placeholder={emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClasses}
            required
          />
        </div>
      </div>

      <div>
        <label htmlFor="ps-location" className="block text-sm font-medium mb-2 text-[#1A1A2E]">
          {locationLabel}
        </label>
        <input
          id="ps-location"
          type="text"
          placeholder={locationPlaceholder}
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className={inputClasses}
        />
      </div>

      <div>
        <span className="block text-sm font-medium mb-3 text-[#1A1A2E]">
          {servicesLabel}
        </span>
        <div className="flex flex-wrap gap-2">
          {serviceOptions.map((service) => {
            const active = services.has(service);
            return (
              <button
                key={service}
                type="button"
                onClick={() => toggleService(service)}
                aria-pressed={active}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors duration-200 cursor-pointer border',
                  active
                    ? 'bg-[#9B7B3A] text-white border-[#9B7B3A]'
                    : 'bg-white text-[#5A5A6E] border-[#5A5A6E]/20 hover:border-[#9B7B3A] hover:text-[#1A1A2E]'
                )}
              >
                {active && <Check className="w-3.5 h-3.5" />}
                {service}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label htmlFor="ps-message" className="block text-sm font-medium mb-2 text-[#1A1A2E]">
          {messageLabel}
        </label>
        <textarea
          id="ps-message"
          rows={5}
          placeholder={messagePlaceholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className={inputClasses}
        />
      </div>

      <Button className="w-full sm:w-auto">{submitLabel}</Button>
    </form>
  );
}
